"use client";

import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";

type Appearance = "dark" | "light";
const key = "deepface-home-appearance";
const changed = "deepface-home-appearance-change";
let fallback: Appearance = "dark";
function snapshot(): Appearance {
  try { const value = localStorage.getItem(key); return value === "light" || value === "dark" ? value : fallback; }
  catch { return fallback; }
}
function subscribe(update: () => void) {
  const storage = (event: StorageEvent) => { if (event.key === key || event.key === null) update(); };
  window.addEventListener(changed, update);
  window.addEventListener("storage", storage);
  return () => { window.removeEventListener(changed, update); window.removeEventListener("storage", storage); };
}
const AppearanceContext = createContext<Appearance>("dark");
export default function HomeAppearance({ children }: { children: ReactNode }) {
  const appearance = useSyncExternalStore(subscribe, snapshot, () => "dark" as Appearance);
  return <AppearanceContext.Provider value={appearance}><div className="conversion-site min-h-screen" data-appearance={appearance} suppressHydrationWarning>
    {/* Apply the saved homepage preference before paint; React takes over after hydration. */}
    <script dangerouslySetInnerHTML={{ __html: `(function(){try{var t=localStorage.getItem("${key}");if(t==="light"||t==="dark")document.currentScript.parentElement.dataset.appearance=t;}catch(e){}})();` }} />
    {children}
  </div></AppearanceContext.Provider>;
}
export function useHomeAppearance() { return useContext(AppearanceContext); }
export function HomeAppearanceToggle({ placement = "header" }: { placement?: "header" | "footer" }) {
  const appearance = useContext(AppearanceContext);
  const next = appearance === "dark" ? "light" : "dark";
  return <Button type="button" variant="ghost" size={placement === "footer" ? "default" : "icon"} className={`conversion-theme-toggle conversion-theme-toggle-${placement}`} aria-label={`Switch to ${next} mode`} title={`Switch to ${next} mode`} onClick={() => {
    try { localStorage.setItem(key, next); fallback = "dark"; } catch { fallback = next; }
    window.dispatchEvent(new Event(changed));
  }}>{appearance === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}{placement === "footer" ? `${next === "light" ? "Light" : "Dark"} mode` : null}</Button>;
}
